import { startOfDay } from "date-fns";
import { formatInTimeZone, utcToZonedTime, format } from "date-fns-tz";

export const getDateRangeForQueries = (clientTimezone: string) => {
  // Get current date in client timezone
  const nowInClientTz = utcToZonedTime(new Date(), clientTimezone);
  const startOfToday = startOfDay(nowInClientTz);

  const startOfDayClientTz = utcToZonedTime(startOfToday, clientTimezone);
  const endOfDayClientTz = utcToZonedTime(
    new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1),
    clientTimezone,
  );

  // Format for ISO string queries (keeping timezone info)
  const startDateIso = formatInTimeZone(
    startOfDayClientTz,
    clientTimezone,
    "yyyy-MM-dd'T'00:00:00XXX", // Note: using XXX to include timezone offset
  );

  const endDateIso = formatInTimeZone(
    endOfDayClientTz,
    clientTimezone,
    "yyyy-MM-dd'T'23:59:59XXX",
  );

  const dateOnlyFormat = formatInTimeZone(
    startOfDayClientTz,
    clientTimezone,
    "yyyy-MM-dd",
  );

  return {
    iso: {
      start: startDateIso,
      end: endDateIso,
    },
    dateOnly: dateOnlyFormat,
  };
};

// This function works around the fact that calendar items are stored in UTC (when in fact they reflect the clients tz),
// but appointments are stored in the client's timezone.
export const getTimeValueForSort = (
  date: Date,
  clientTimezone: string,
  isCalendarItem: boolean,
) => {
  // For calendar items, treat UTC time as if it were local time
  if (isCalendarItem) {
    // Extract hours and minutes from UTC time since it represents local time
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    return hours * 60 + minutes;
  }

  const localDate = utcToZonedTime(date, clientTimezone);
  const hours = localDate.getHours();
  const minutes = localDate.getMinutes();
  return hours * 60 + minutes;
};

/**
 * Handles timezone conversions for different event types:
 *
 * Calendar Items:
 * - Stored in UTC but actually reflect client timezone
 *
 * Appointments:
 * - Stored in UTC
 * - Need conversion to client timezone
 *
 * Google Calendar Events:
 * - Stored with timezone offset
 * - Need conversion to client timezone
 */
export type DateTimeConverter = {
  toClientDate: (dateStr: string, clientTimeZone: string) => Date;
  toClientString: (dateStr: string, clientTimeZone: string) => string;
  toClientTimeString: (dateStr: string, clientTimeZone: string) => string;
};

// For appointments stored in UTC but need conversion to client timezone
export const appointmentDateTime: DateTimeConverter = {
  toClientDate: (dateStr: string, clientTimeZone: string) => {
    const utcDate = new Date(dateStr);
    return utcToZonedTime(utcDate, clientTimeZone);
  },
  toClientString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = appointmentDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "MMMM d, yyyy 'at' h:mm a", {
      timeZone: clientTimeZone,
    });
  },
  toClientTimeString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = appointmentDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "h:mm a", { timeZone: clientTimeZone });
  },
};

// For calendar_items stored as UTC but representing client timezone time
export const calendarItemDateTime: DateTimeConverter = {
  toClientDate: (dateStr: string) => {
    const [datePart, timePart] = dateStr.split("T");
    const timeWithoutZ = timePart.replace("Z", "");
    return new Date(`${datePart}T${timeWithoutZ}`);
  },
  toClientString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = calendarItemDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "MMMM d, yyyy 'at' h:mm a", {
      // eslint-disable-next-line
      timeZone: clientTimeZone,
    });
  },
  toClientTimeString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = calendarItemDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "h:mm a", { timeZone: clientTimeZone });
  },
};

// For Google Calendar events with timezone offset
export const googleCalendarDateTime: DateTimeConverter = {
  toClientDate: (dateStr: string, clientTimeZone: string) => {
    const date = new Date(dateStr);
    return utcToZonedTime(date, clientTimeZone);
  },
  toClientString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = googleCalendarDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "MMMM d, yyyy 'at' h:mm a", {
      timeZone: clientTimeZone,
    });
  },
  toClientTimeString: (dateStr: string, clientTimeZone: string) => {
    const clientDate = googleCalendarDateTime.toClientDate(
      dateStr,
      clientTimeZone,
    );
    return format(clientDate, "h:mm a", { timeZone: clientTimeZone });
  },
};
